import { useState } from "react";
import { useEffect } from "react";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DatePicker } from "./DatePicker";
import { UserMinus, UserCheck, Loader2 } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const MOTIVI_COMUNI = [
    "Dimissioni",
    "Pensionamento",
    "Licenziamento per giusta causa",
    "Licenziamento per giustificato motivo soggettivo",
    "Licenziamento per giustificato motivo oggettivo",
    "Risoluzione consensuale",
    "Scadenza contratto a termine",
    "Decesso",
];

export const CessationDialog = ({ open, onOpenChange, employee, onSaved }) => {
    const [date, setDate] = useState("");
    const [motivo, setMotivo] = useState("");
    const [saving, setSaving] = useState(false);

    // Edit mode: the employee is already terminated → prefill and relabel
    const isEdit = !!employee?.data_cessazione;

    useEffect(() => {
        if (open) {
            setDate(employee?.data_cessazione || "");
            setMotivo(employee?.motivo_cessazione || "");
        }
    }, [open, employee]);

    const handleSave = async () => {
        if (!date) {
            toast.error("Seleziona la data di cessazione");
            return;
        }
        if (!motivo || !motivo.trim()) {
            toast.error("Indica il motivo della cessazione");
            return;
        }
        setSaving(true);
        try {
            await api.put(`/employees/${employee.id}`, {
                data_cessazione: date,
                motivo_cessazione: motivo.trim(),
            });
            toast.success(
                isEdit
                    ? "Dati di cessazione aggiornati"
                    : `${employee.cognome} ${employee.nome} segnato come cessato`
            );
            onSaved && onSaved();
            onOpenChange(false);
        } catch (e) {
            toast.error("Errore durante l'aggiornamento");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="cessation-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2 text-amber-700">
                        <UserMinus className="h-5 w-5" />
                        {isEdit ? "Modifica cessazione" : "Segna come cessato"}
                    </DialogTitle>
                    <DialogDescription>
                        {isEdit ? (
                            <>Correggi la data o il motivo di cessazione di {employee?.cognome} {employee?.nome} (matricola {employee?.matricola}).</>
                        ) : (
                            <>
                                {employee?.cognome} {employee?.nome} (matricola {employee?.matricola}) verrà spostato tra i cessati.
                                Le credenziali di login restano attive per consentirgli di accedere ai documenti storici.
                            </>
                        )}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div>
                        <Label htmlFor="cessation-date">Data di cessazione *</Label>
                        <div className="mt-1.5">
                            <DatePicker
                                value={date}
                                onChange={setDate}
                                placeholder="Seleziona la data"
                                data-testid="cessation-date"
                            />
                        </div>
                    </div>
                    <div>
                        <Label htmlFor="cessation-motivo">Motivo *</Label>
                        <Input
                            id="cessation-motivo"
                            list="motivi-comuni"
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Es. Dimissioni, Pensionamento…"
                            className="mt-1.5"
                            data-testid="cessation-motivo"
                            required
                        />
                        <datalist id="motivi-comuni">
                            {MOTIVI_COMUNI.map((m) => (
                                <option key={m} value={m} />
                            ))}
                        </datalist>
                        <div className="mt-1 text-xs text-slate-500">
                            Inizia a digitare per vedere i suggerimenti
                        </div>
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="cessation-cancel">
                        Annulla
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving || !date || !motivo.trim()}
                        className="gap-2 bg-amber-600 text-white hover:bg-amber-700"
                        data-testid="cessation-save"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserMinus className="h-4 w-4" />}
                        {isEdit ? "Salva modifiche" : "Conferma cessazione"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export const ReactivateDialog = ({ open, onOpenChange, employee, onSaved }) => {
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        setSaving(true);
        try {
            const { data } = await api.put(`/employees/${employee.id}`, {
                data_cessazione: null,
                motivo_cessazione: null,
                portal_access_until: null,
            });
            if (data?.data_cessazione) {
                toast.error("Riattivazione non riuscita: i dati di cessazione sono ancora presenti");
                return;
            }
            toast.success(`${employee.cognome} ${employee.nome} riattivato`);
            onSaved && onSaved();
            onOpenChange(false);
        } catch (e) {
            toast.error("Errore durante la riattivazione");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="reactivate-dialog">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2 text-emerald-700">
                        <UserCheck className="h-5 w-5" />
                        Riattiva dipendente
                    </DialogTitle>
                    <DialogDescription>
                        {employee?.cognome} {employee?.nome} (matricola {employee?.matricola})
                        verrà rimesso tra i dipendenti attivi. La data di cessazione e il motivo
                        verranno cancellati.
                    </DialogDescription>
                </DialogHeader>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reactivate-cancel">
                        Annulla
                    </Button>
                    <Button
                        onClick={handleSave}
                        disabled={saving}
                        className="gap-2 bg-emerald-600 text-white hover:bg-emerald-700"
                        data-testid="reactivate-save"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserCheck className="h-4 w-4" />}
                        Conferma riattivazione
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
